import clarity from "@microsoft/clarity";
import { AnalyticsBrowser } from "@segment/analytics-next";
import posthog from "posthog-js";
import { SEGMENT_INIT_OPTIONS } from "./segmentInitOptions";

type BufferedEvent =
  | { type: "track"; eventName: string; properties?: Record<string, unknown> }
  | { type: "identify"; userId: string; traits?: Record<string, unknown> }
  | { type: "page"; name?: string; properties?: Record<string, unknown> };

let analytics: AnalyticsBrowser | null = null;
let postHogEnabled = false;
let clarityEnabled = false;
const eventBuffer: BufferedEvent[] = [];

const flushBuffer = (): void => {
  let event = eventBuffer.shift();
  while (event) {
    switch (event.type) {
      case "track":
        rawTrack(event.eventName, event.properties);
        break;
      case "identify":
        rawIdentify(event.userId, event.traits);
        break;
      case "page":
        rawPage(event.name, event.properties);
        break;
    }
    event = eventBuffer.shift();
  }
};

export const initSegment = (writeKey: string): void => {
  if (analytics || typeof window === "undefined" || !writeKey) return;
  analytics = AnalyticsBrowser.load({ writeKey }, SEGMENT_INIT_OPTIONS);
  flushBuffer();
};

export const initPostHog = (apiKey: string): void => {
  if (postHogEnabled || typeof window === "undefined" || !apiKey) return;
  posthog.init(apiKey, {
    api_host:
      process.env.NEXT_PUBLIC_POSTHOG_HOST ?? "https://us.i.posthog.com",
    autocapture: false,
    capture_pageview: false,
    enable_heatmaps: true,
    defaults: "2026-01-30",
  });
  postHogEnabled = true;
};

export const rawTrack = (
  eventName: string,
  properties?: Record<string, unknown>,
): void => {
  if (!analytics) {
    eventBuffer.push({ type: "track", eventName, properties });
    return;
  }
  try {
    analytics.track(eventName, properties);
  } catch (error) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("[analytics:core]", error);
    }
  }
};

export const rawIdentify = (
  userId: string,
  traits?: Record<string, unknown>,
): void => {
  if (!analytics) {
    eventBuffer.push({ type: "identify", userId, traits });
    return;
  }
  try {
    analytics.identify(userId, traits);
  } catch (error) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("[analytics:core]", error);
    }
  }
};

export const rawPage = (
  name?: string,
  properties?: Record<string, unknown>,
): void => {
  if (!analytics) {
    eventBuffer.push({ type: "page", name, properties });
    return;
  }
  try {
    analytics.page(name, properties);
  } catch (error) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("[analytics:core]", error);
    }
  }
};

export const rawPostHogCapture = (
  eventName: string,
  properties?: Record<string, unknown>,
): void => {
  if (!postHogEnabled) return;
  try {
    posthog.capture(eventName, properties);
  } catch (error) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("[analytics:core]", error);
    }
  }
};

export const rawPostHogIdentify = (
  userId: string,
  traits?: Record<string, unknown>,
): void => {
  if (!postHogEnabled) return;
  try {
    posthog.identify(userId, traits);
  } catch (error) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("[analytics:core]", error);
    }
  }
};

export const initClarity = (projectId: string): void => {
  if (clarityEnabled || typeof window === "undefined" || !projectId) return;
  try {
    clarity.init(projectId);
    clarityEnabled = true;
  } catch (error) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("[analytics:core]", error);
    }
  }
};

export const setClarityTags = (tags: {
  tenantId?: string;
  hotelId?: string;
}): void => {
  if (!clarityEnabled) return;
  try {
    if (tags.tenantId) clarity.setTag("tenant_id", tags.tenantId);
    if (tags.hotelId) clarity.setTag("hotel_id", tags.hotelId);
  } catch (error) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("[analytics:core]", error);
    }
  }
};
